/**
 * FicheParcellePage — tout ce que l'appli sait d'une parcelle, avec la source de chaque valeur et ce qui manque.
 *
 * URL : /parcelles/:parcelId/fiche (paramètres facultatifs ?ilot_id=&start=&end=)
 * Cahier des charges : docs/superpowers/specs/2026-10-29-fiche-parcelle-complete-design.md (lot 1).
 * Téléphone (< 1024 px) : cartes empilées et barre d'actions en bas. Ordinateur : grille et panneau d'extraction à droite.
 * Hors réseau : la dernière fiche chargée sur l'appareil (IndexedDB) s'affiche avec sa date.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Download, FileDown, Loader2, Lock, Map as MapIcon, Printer, User, WifiOff } from "lucide-react";
import { authFetch } from "@/lib/api";
import { ficheKey, loadFiche, saveFiche } from "@/lib/ficheCache";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { API, COL, dateFr, isErreur, nombre, telecharger } from "@/components/fiche/shared";
import FicheMiniCarte from "@/components/fiche/FicheMiniCarte";
import CompletudeCard from "@/components/fiche/CompletudeCard";
import ExploitationCard from "@/components/fiche/ExploitationCard";
import LocalisationCard from "@/components/fiche/LocalisationCard";
import IlotsCard from "@/components/fiche/IlotsCard";
import SolCard from "@/components/fiche/SolCard";
import InterventionsCard from "@/components/fiche/InterventionsCard";
import ConformiteCard from "@/components/fiche/ConformiteCard";
import SanteCard from "@/components/fiche/SanteCard";
import HistoriqueCard from "@/components/fiche/HistoriqueCard";
import DocumentsCard from "@/components/fiche/DocumentsCard";
import ExtractionPanel, { pdfUrl } from "@/components/fiche/ExtractionPanel";

const DESKTOP = "(min-width: 1024px)";

function useIsDesktop() {
  const [desktop, setDesktop] = useState(() => window.matchMedia(DESKTOP).matches);
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP);
    const on = () => setDesktop(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return desktop;
}

// Paramètres de la vue transmis à l'API (îlot, période)
export function useFicheQuery() {
  const [searchParams] = useSearchParams();
  const q = new URLSearchParams();
  ["ilot_id", "start", "end"].forEach((k) => searchParams.get(k) && q.set(k, searchParams.get(k)));
  return q.toString();
}

export function useFiche(parcelId, query) {
  const [state, setState] = useState({ fiche: null, horsLigne: null, erreur: null, loading: true });
  useEffect(() => {
    let annule = false;
    const key = ficheKey(parcelId, query);
    const done = (s) => !annule && setState({ fiche: null, horsLigne: null, erreur: null, loading: false, ...s });
    (async () => {
      try {
        const res = await authFetch(`${API}/parcels/${encodeURIComponent(parcelId)}/fiche${query ? `?${query}` : ""}`);
        if (res.ok) {
          const fiche = await res.json();
          saveFiche(key, fiche);
          return done({ fiche });
        }
        if ([400, 401, 403, 404].includes(res.status)) return done({ erreur: res.status });
        throw new Error(String(res.status));
      } catch {
        const cache = await loadFiche(key);
        return cache ? done({ fiche: cache.fiche, horsLigne: cache.saved_at }) : done({ erreur: "reseau" });
      }
    })();
    return () => { annule = true; };
  }, [parcelId, query]);
  return state;
}

function Chip({ children }) {
  return <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ background: COL.greenSoft, color: COL.green }}>{children}</span>;
}

function entete(fiche) {
  const loc = isErreur(fiche.localisation) ? {} : fiche.localisation;
  const ilots = Array.isArray(fiche.ilots) ? fiche.ilots : [];
  const expl = isErreur(fiche.exploitation) ? {} : fiche.exploitation;
  const surface = loc.surface_rpg || loc.surface_calculee;
  const famille = ilots.map((i) => i.culture?.valeur?.famille).find(Boolean);
  const mode = expl.mode_production?.valeur;
  return {
    nom: loc.nom?.valeur || fiche.parcel_id,
    sousTitre: [loc.commune?.valeur, loc.altitude ? `${nombre(loc.altitude.valeur, 0)} m` : null, loc.type_culture?.valeur]
      .filter(Boolean).join(" · "),
    chips: [
      surface && `${nombre(surface.valeur)} ha`,
      ilots.length && ilots[0].ilot_id ? `${ilots.length} îlot${ilots.length > 1 ? "s" : ""}` : "Sans îlot",
      famille,
      mode === "bio" ? "Bio" : mode === "conventionnel" ? "Conventionnel" : null,
    ].filter(Boolean),
  };
}

function Bandeaux({ fiche, horsLigne }) {
  const p = fiche.periode;
  return (
    <>
      {horsLigne && (
        <div className="flex items-center gap-2 text-sm rounded-xl px-3 py-2 mb-3" style={{ background: COL.amberSoft, color: COL.amber }}
          data-testid="fiche-hors-ligne">
          <WifiOff className="w-4 h-4 shrink-0" /> Fiche à jour sur ce téléphone le {new Date(horsLigne).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
        </div>
      )}
      <p className="text-xs text-gray-500 mb-3">
        {p.type === "campagne" ? "Campagne en cours" : p.type === "annee" ? `Année ${p.debut.slice(0, 4)}` : "Période"} :
        du {dateFr(p.debut)} au {dateFr(p.fin)}
      </p>
    </>
  );
}

function Rubriques({ fiche, desktop }) {
  const pid = fiche.parcel_id;
  const common = [
    <ExploitationCard key="e" data={fiche.exploitation} />,
    <LocalisationCard key="l" data={fiche.localisation} parcelId={pid} />,
  ];
  if (!desktop) {
    return (
      <div className="space-y-3">
        {common}
        <IlotsCard data={fiche.ilots} />
        <SolCard data={fiche.sol} />
        <InterventionsCard data={fiche.interventions} journal={fiche.journal} periode={fiche.periode} />
        <ConformiteCard data={fiche.conformite} />
        <SanteCard data={fiche.sante} />
        <HistoriqueCard data={fiche.historique} />
        <DocumentsCard data={fiche.documents} parcelId={pid} />
      </div>
    );
  }
  return (
    <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))" }}>
      {common}
      <SolCard data={fiche.sol} />
      <IlotsCard data={fiche.ilots} desktop className="col-span-full" />
      <InterventionsCard data={fiche.interventions} journal={fiche.journal} periode={fiche.periode} desktop className="col-span-full" />
      <ConformiteCard data={fiche.conformite} />
      <SanteCard data={fiche.sante} />
      <HistoriqueCard data={fiche.historique} />
      <DocumentsCard data={fiche.documents} parcelId={pid} />
    </div>
  );
}

function Message({ erreur, parcelId, onBack }) {
  const textes = {
    403: ["Accès réservé", "Cette parcelle est verrouillée par un autre compte. Demandez l'accès depuis son carnet."],
    404: ["Parcelle introuvable", "Cette parcelle n'existe pas ou a été supprimée."],
    401: ["Session expirée", "Reconnectez-vous pour afficher la fiche."],
    400: ["Paramètres invalides", "La période ou l'îlot demandés ne sont pas valides."],
    reseau: ["Pas de réseau", "La fiche n'a jamais été ouverte sur cet appareil. Réessayez avec du réseau."],
  };
  const [titre, texte] = textes[erreur] || textes.reseau;
  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: COL.bg }} data-testid="fiche-erreur">
      <div className="max-w-sm text-center">
        <Lock className="w-8 h-8 mx-auto mb-3" style={{ color: COL.green }} />
        <h1 className="text-lg font-bold mb-1">{titre}</h1>
        <p className="text-sm text-gray-600 mb-4">{texte}</p>
        <Button variant="outline" className="rounded-full h-11" onClick={onBack}>Retour</Button>
        {erreur === 403 && <p className="text-xs text-gray-400 mt-3 font-mono">{parcelId}</p>}
      </div>
    </div>
  );
}

export default function FicheParcellePage() {
  const { parcelId } = useParams();
  const navigate = useNavigate();
  const desktop = useIsDesktop();
  const query = useFicheQuery();
  const { fiche, horsLigne, erreur, loading } = useFiche(parcelId, query);
  const [panneau, setPanneau] = useState(false);
  const carnet = `/carnet/${encodeURIComponent(parcelId)}`;
  const pdf = () => telecharger(pdfUrl(parcelId, query), `veyerciel_${parcelId}_fiche.pdf`);
  const extraire = () => (desktop ? setPanneau(!panneau) : navigate(`/parcelles/${encodeURIComponent(parcelId)}/extraction${query ? `?${query}` : ""}`));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: COL.bg }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: COL.green }} />
      </div>
    );
  }
  if (erreur) return <Message erreur={erreur} parcelId={parcelId} onBack={() => navigate(erreur === 403 ? "/dashboard" : carnet)} />;

  const { nom, sousTitre, chips } = entete(fiche);
  const ilots = Array.isArray(fiche.ilots) ? fiche.ilots : [];
  const geometry = isErreur(fiche.localisation) ? null : fiche.localisation.geometry;

  if (!desktop) {
    return (
      <div className="min-h-screen" style={{ background: COL.bg }} data-testid="fiche-parcelle-page">
        <header className="sticky top-safe z-[500] bg-white/95 backdrop-blur border-b px-3" style={{ borderColor: COL.line }}>
          <div className="flex items-start gap-2 py-2">
            <button type="button" onClick={() => navigate(carnet)} className="w-11 h-11 -ml-1 flex items-center justify-center rounded-full hover:bg-black/5 shrink-0"
              aria-label="Retour au carnet" data-testid="fiche-retour">
              <ArrowLeft className="w-5 h-5" style={{ color: COL.green }} />
            </button>
            <div className="min-w-0 flex-1">
              <div className="font-mono text-[11px] text-gray-500 truncate">{fiche.parcel_id}</div>
              <h1 className="text-lg font-bold leading-tight truncate" style={{ fontFamily: "Manrope, sans-serif" }}>{nom}</h1>
              {sousTitre && <div className="text-xs text-gray-600 truncate">{sousTitre}</div>}
              <div className="flex flex-wrap gap-1.5 mt-1.5 mb-1">{chips.map((c) => <Chip key={c}>{c}</Chip>)}</div>
            </div>
          </div>
        </header>
        <main className="px-4 pt-3 pb-28 space-y-3">
          <Bandeaux fiche={fiche} horsLigne={horsLigne} />
          <FicheMiniCarte parcelId={parcelId} geometry={geometry} ilots={ilots} />
          <CompletudeCard completude={fiche.completude} />
          <Rubriques fiche={fiche} desktop={false} />
        </main>
        <div className="fixed bottom-0 inset-x-0 z-[500] bg-white/95 backdrop-blur border-t px-4 pt-2 pb-safe flex gap-2" style={{ borderColor: COL.line }}>
          <Button className="flex-1 h-12 rounded-full text-sm font-semibold text-white gap-2 mb-2" style={{ background: COL.green }}
            onClick={extraire} data-testid="fiche-extraire">
            <Download className="w-4 h-4" /> Extraire les données
          </Button>
          <Button variant="outline" className="h-12 px-5 rounded-full text-sm font-semibold gap-2 mb-2" onClick={pdf} data-testid="fiche-pdf">
            <FileDown className="w-4 h-4" /> PDF
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: COL.bg }} data-testid="fiche-parcelle-page">
      <header className="sticky top-safe z-[500] backdrop-blur-md border-b" style={{ background: "rgba(249,248,246,0.9)", borderColor: COL.line }}>
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <button type="button" onClick={() => navigate("/dashboard")} className="shrink-0" aria-label="Tableau de bord">
            <img src="/veyer-ciel-logo.png" alt="Véyèr CIEL" className="h-9" />
          </button>
          <nav className="flex items-center gap-2">
            <Button variant="outline" className="h-9 px-4 rounded-full text-xs gap-2" onClick={() => navigate("/parcelles")}>
              <BookOpen className="w-3.5 h-3.5" /> Mes parcelles
            </Button>
            <Button variant="outline" className="h-9 px-4 rounded-full text-xs gap-2" onClick={() => navigate("/map")}>
              <MapIcon className="w-3.5 h-3.5" /> Carte
            </Button>
            <Button variant="outline" className="h-9 px-4 rounded-full text-xs gap-2" onClick={() => navigate("/account")}>
              <User className="w-3.5 h-3.5" /> Mon compte
            </Button>
          </nav>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-6 py-5">
        <Breadcrumb className="mb-3">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink className="cursor-pointer" onClick={() => navigate("/parcelles")}>Mes parcelles</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage>{nom}</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex items-end justify-between gap-4 mb-4">
          <div className="min-w-0">
            <div className="font-mono text-xs text-gray-500">{fiche.parcel_id}</div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>{nom}</h1>
            {sousTitre && <div className="text-sm text-gray-600">{sousTitre}</div>}
            <div className="flex flex-wrap gap-1.5 mt-2">{chips.map((c) => <Chip key={c}>{c}</Chip>)}</div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" className="h-11 rounded-full gap-2" onClick={() => navigate(carnet)} data-testid="fiche-ouvrir-carnet">
              <BookOpen className="w-4 h-4" /> Ouvrir le carnet
            </Button>
            <Button variant="outline" className="h-11 rounded-full gap-2" onClick={pdf} data-testid="fiche-pdf">
              <Printer className="w-4 h-4" /> Imprimer le PDF
            </Button>
            <Button className="h-11 rounded-full gap-2 text-white" style={{ background: COL.green }} onClick={extraire} data-testid="fiche-extraire">
              <Download className="w-4 h-4" /> Extraire les données
            </Button>
          </div>
        </div>
        <Bandeaux fiche={fiche} horsLigne={horsLigne} />
        <div className="flex gap-5 items-start">
          <main className="flex-1 min-w-0 space-y-4">
            <div className="grid gap-4" style={{ gridTemplateColumns: "minmax(0,3fr) minmax(0,2fr)" }}>
              <FicheMiniCarte parcelId={parcelId} geometry={geometry} ilots={ilots} className="h-72" />
              <CompletudeCard completude={fiche.completude} className="max-h-72 overflow-y-auto" />
            </div>
            <Rubriques fiche={fiche} desktop />
          </main>
          {panneau && (
            <ExtractionPanel parcelId={parcelId} fiche={fiche} onClose={() => setPanneau(false)}
              className="w-[380px] shrink-0 sticky top-20 max-h-[calc(100dvh-6rem)] overflow-y-auto" />
          )}
        </div>
      </div>
    </div>
  );
}
